import React, { useCallback, useEffect, useMemo, useState } from 'react';
import moment from 'moment/min/moment-with-locales';
import { api } from 'common';
import { Alert, Box, Grid, Typography } from '@mui/material';
import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import VolunteerActivismOutlinedIcon from '@mui/icons-material/VolunteerActivismOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import { C2SecondaryButton } from '../C2Dialog';
import { C2, C2_FONT, C2Select, C2SummaryCard, C2StatusChip } from '../Table/C2UserTable';
import C2ReportTable, { C2Cell, formatCop } from './C2ReportTable';

const TOP_N = 100;
const EMPTY_MONTH = { billed: [0, 0, 0], recharges: [0, 0, 0], bonus: [0, 0, 0] };
const sum = (arr) => (arr || []).reduce((acc, v) => acc + (Number(v) || 0), 0);

const roleLabel = (d) => {
  if (d.isLeader) return 'Líder';
  if (d.networkLevel != null) return `Nivel ${d.networkLevel}`;
  return 'Independiente';
};

/**
 * Auditoría admin: top 100 conductores por bono de referidos ganado en el mes,
 * filtrable por ciudad. Incluye saldo actual walletReferral.
 */
export default function BonusAuditPanel() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [month, setMonth] = useState('');
  const [city, setCity] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const res = await api.fetchNetworkLevelReport();
    setLoading(false);
    if (res?.success) {
      setReport(res);
      setMonth((cur) => (cur && res.months.includes(cur) ? cur : res.months[0]));
    } else {
      setError(res?.error || 'No se pudo generar la auditoría de bonus.');
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const monthOpts = useMemo(
    () => (report?.months || []).map((mk) => ({ value: mk, label: api.reportMonthLabel(mk) })),
    [report]
  );

  const cityOpts = useMemo(() => {
    const map = {};
    (report?.drivers || []).forEach((d) => {
      if (d.cityId) map[d.cityId] = d.cityName || d.cityId;
    });
    return [
      { value: '', label: 'Todas las ciudades' },
      ...Object.keys(map)
        .map((id) => ({ value: id, label: map[id] }))
        .sort((a, b) => a.label.localeCompare(b.label, 'es')),
    ];
  }, [report]);

  const rows = useMemo(() => {
    if (!report || !month) return [];
    return report.drivers
      .filter((d) => !city || d.cityId === city)
      .map((d) => {
        const m = d.months[month] || EMPTY_MONTH;
        const totalBonus = sum(m.bonus);
        return {
          id: d.uid,
          driver: d,
          name: d.name,
          email: d.email || '',
          mobile: d.mobile || '',
          cityName: d.cityName || '',
          role: roleLabel(d),
          l1Bonus: m.bonus[0] || 0,
          l2Bonus: m.bonus[1] || 0,
          l3Bonus: m.bonus[2] || 0,
          totalBonus,
          walletReferral: Number(d.walletReferral) || 0,
        };
      })
      .filter((r) => r.totalBonus > 0 || r.walletReferral > 0)
      .sort((a, b) => b.totalBonus - a.totalBonus || b.walletReferral - a.walletReferral || a.name.localeCompare(b.name, 'es'))
      .slice(0, TOP_N)
      .map((r, idx) => ({ ...r, rank: idx + 1 }));
  }, [report, month, city]);

  const summary = useMemo(() => {
    const monthBonus = rows.reduce((acc, r) => acc + r.totalBonus, 0);
    const walletSum = rows.reduce((acc, r) => acc + r.walletReferral, 0);
    const withBonus = rows.filter((r) => r.totalBonus > 0).length;
    return { count: rows.length, withBonus, monthBonus, walletSum };
  }, [rows]);

  const monthLabel = month ? api.reportMonthLabel(month) : '—';

  const columns = [
    {
      title: '#',
      field: 'rank',
      width: 56,
      render: (r) => (
        <Typography sx={{ fontFamily: C2_FONT, fontWeight: 800, fontSize: '0.95rem', color: r.rank <= 3 ? C2.violet : C2.sidebar }}>
          {r.rank}
        </Typography>
      ),
    },
    {
      title: 'Conductor',
      field: 'name',
      render: (r) => (
        <Box sx={{ textAlign: 'left', minWidth: 170 }}>
          <Typography sx={{ fontFamily: C2_FONT, fontWeight: 700, fontSize: '0.9rem', color: C2.sidebar }}>{r.name}</Typography>
          <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.75rem', color: C2.muted, mb: 0.5 }}>
            {r.cityName || 'Sin ciudad'}
          </Typography>
          <C2StatusChip tone={r.driver.isLeader ? 'violet' : 'neutral'} dot={false} label={r.role} />
        </Box>
      ),
    },
    { title: 'Correo', field: 'email', hidden: true, export: true },
    { title: 'Teléfono', field: 'mobile', hidden: true, export: true },
    { title: 'Ciudad', field: 'cityName', hidden: true, export: true },
    { title: 'Rol', field: 'role', hidden: true, export: true },
    {
      title: 'Bono N1 (8%)',
      field: 'l1Bonus',
      type: 'numeric',
      render: (r) => <C2Cell primary={formatCop(r.l1Bonus)} color={r.l1Bonus ? C2.sidebar : C2.muted} />,
    },
    {
      title: 'Bono N2 (6%)',
      field: 'l2Bonus',
      type: 'numeric',
      render: (r) => <C2Cell primary={formatCop(r.l2Bonus)} color={r.l2Bonus ? C2.sidebar : C2.muted} />,
    },
    {
      title: 'Bono N3 (2%)',
      field: 'l3Bonus',
      type: 'numeric',
      render: (r) => <C2Cell primary={formatCop(r.l3Bonus)} color={r.l3Bonus ? C2.sidebar : C2.muted} />,
    },
    {
      title: 'Bono del mes',
      field: 'totalBonus',
      type: 'numeric',
      render: (r) => <C2Cell primary={formatCop(r.totalBonus)} color={r.totalBonus ? '#15803D' : C2.muted} />,
    },
    {
      title: 'Saldo bonus actual',
      field: 'walletReferral',
      type: 'numeric',
      render: (r) => (
        <C2Cell primary={formatCop(r.walletReferral)} color={r.walletReferral ? C2.violet : C2.muted} />
      ),
    },
  ];

  return (
    <>
      {error ? (
        <Alert
          severity="error"
          sx={{ mb: 2, borderRadius: '12px', fontFamily: C2_FONT }}
          action={
            <C2SecondaryButton size="small" onClick={load}>
              Reintentar
            </C2SecondaryButton>
          }
        >
          {error}
        </Alert>
      ) : null}

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} lg={3}>
          <C2SummaryCard
            icon={EmojiEventsOutlinedIcon}
            label="Top conductores"
            value={summary.count}
            caption={`Máximo ${TOP_N} · ${monthLabel}`}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <C2SummaryCard
            icon={GroupsOutlinedIcon}
            label="Con bono en el mes"
            value={summary.withBonus}
            caption={city ? 'En la ciudad seleccionada' : 'Todas las ciudades'}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <C2SummaryCard
            icon={VolunteerActivismOutlinedIcon}
            label="Bono ganado (top)"
            value={formatCop(summary.monthBonus)}
            caption={monthLabel}
            accent="#16A34A"
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <C2SummaryCard
            icon={AccountBalanceWalletOutlinedIcon}
            label="Saldo bonus (top)"
            value={formatCop(summary.walletSum)}
            caption="Suma walletReferral del ranking"
          />
        </Grid>
      </Grid>

      <C2ReportTable
        title="Auditoría de Bonus"
        subtitle="Los 100 conductores con más bono de referidos ganado en el mes. Filtra por ciudad; el saldo actual es el walletReferral disponible para retiro."
        columns={columns}
        data={rows}
        loading={loading}
        emptyIcon={EmojiEventsOutlinedIcon}
        emptyTitle={report ? 'Sin conductores con bonus' : 'Cargando auditoría…'}
        emptyMessage={
          report
            ? 'Cuando se acrediten bonos de red en el mes seleccionado, el ranking aparecerá aquí.'
            : null
        }
        csvFileName={`auditoria_bonus_${month || 'mes'}${city ? `_${city}` : ''}.csv`}
        toolbarExtras={[
          <C2Select
            key="m"
            id="bonus-audit-month"
            label="Mes"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            options={monthOpts.length ? monthOpts : [{ value: '', label: '—' }]}
            minWidth={180}
          />,
          <C2Select
            key="c"
            id="bonus-audit-city"
            label="Ciudad"
            value={city}
            onChange={(e) => setCity(e.target.value)}
            options={cityOpts}
            minWidth={190}
          />,
          <C2SecondaryButton
            key="r"
            size="small"
            startIcon={<RefreshRoundedIcon />}
            onClick={load}
            disabled={loading}
            sx={{ height: 40 }}
          >
            Actualizar
          </C2SecondaryButton>,
        ]}
      >
        {report ? (
          <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.74rem', color: C2.muted, px: 0.5, mb: 0.5 }}>
            Ranking por bono del mes (desempate: saldo actual). Generado {moment(report.generatedAt).format('lll')}.
          </Typography>
        ) : null}
      </C2ReportTable>
    </>
  );
}
